/**
 * Adapters: map the backend Category Report / List Report API payloads onto the
 * EXISTING OOR row shapes the table columns, CategoryBar, VitalLegend and the
 * PDF generators already consume. Keeping the render contract identical means
 * the data-source swap touches only the data layer.
 *
 * Backend now owns aggregation, the pending-OOR filter, latest_note, and the
 * canonical sub-category keys, so there is no client-side bucketing here.
 */

import { VITALS, VITALS_BY_KEY, categoryMeta } from "./vitalCategories";

const VITAL_ORDER = VITALS.map((v) => v.key);

/** "2025-10-20" -> "10/20/2025" (Figma DOB format). */
const formatDob = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
};

/** "2026-01-08T07:03:00Z" -> "Jan 8, 2026 7:03AM" (List view date format). */
const formatListDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const datePart = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  let hours = d.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${datePart} ${hours}:${minutes}${ampm}`;
};

const patientFields = (result) => ({
  id: result.patient?.id,
  patientName: result.patient?.name || "",
  dob: formatDob(result.patient?.dob),
  providerId: result.patient?.provider?.id ?? null,
  providerName: result.patient?.provider?.name || "",
  notes: result.latest_note?.text || "",
  noteId: result.latest_note?.id ?? null,
  noteType: null, // backend latest_note has no type; not needed for update
});

/**
 * Category Report -> category rows.
 * Row: { ...patient, vitals: [{ key, label, total, outOfRange, distribution }] }
 * distribution: [{ key, label, color, count, pct }] in canonical order
 * (unknown / "unclassified" keys rendered grey, appended last).
 */
export const adaptCategoryRows = (apiData) => {
  const results = apiData?.results || [];
  return results.map((result) => {
    const categories = result.categories || {};
    const vitals = VITAL_ORDER.filter((key) => categories[key]).map((key) => {
      const block = categories[key];
      const breakdown = block.breakdown || {};
      const pcts = block.breakdown_percentages || {};
      const total = block.total || 0;
      const order = (VITALS_BY_KEY[key]?.categories || []).map((c) => c.key);

      const distribution = Object.entries(breakdown)
        .map(([subKey, count]) => {
          const meta = categoryMeta(key, subKey);
          return {
            key: meta.key,
            label: meta.label,
            color: meta.color,
            count: count || 0,
            pct:
              pcts[subKey] != null
                ? pcts[subKey]
                : total
                ? ((count || 0) / total) * 100
                : 0,
          };
        })
        .filter((d) => d.count > 0)
        .sort((a, b) => {
          const ia = order.indexOf(a.key);
          const ib = order.indexOf(b.key);
          return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        });

      return {
        key,
        label: VITALS_BY_KEY[key]?.label || block.label || key,
        total,
        outOfRange: block.out_of_range_count || 0,
        distribution,
      };
    });

    return { ...patientFields(result), vitals };
  });
};

// Display value per vital from the backend reading. `reading.value` is the
// backend-formatted number/string ("170/100", "220"); we append the unit the
// List view expects.
const formatReadingValue = (reading) => {
  const v = reading.value;
  switch (reading.reading_type) {
    case "blood_pressure":
      return `${v}`; // e.g. "170/100" (no unit, matches Figma)
    case "weight":
      return `${v} lbs`;
    case "temperature":
      return `${v}°F`;
    case "blood_glucose":
      return `${v} mg/dL`;
    case "pulse_ox":
      return `${v}%`;
    default:
      return `${v ?? "--"}`;
  }
};

const buildListItem = (reading) => {
  const vitalKey = reading.reading_type;
  const measurements = reading.measurements || [];
  const hr = measurements.find((m) => m.category === "heart_rate");
  const hrValue = reading.values?.heart_rate;

  return {
    id: reading.id,
    _ts: reading.recorded_at || "",
    dateText: formatListDateTime(reading.recorded_at),
    value: formatReadingValue(reading),
    color: categoryMeta(vitalKey, reading.sub_category).color,
    secondary:
      hrValue != null
        ? {
            value: `${hrValue}bpm`,
            color: categoryMeta("heart_rate", hr?.sub_category).color,
          }
        : null,
    // Category dots: one per measurement on the reading (BP carries both a
    // blood_pressure and a heart_rate measurement).
    categories: measurements.map((m) => ({
      label: m.sub_category_label || categoryMeta(m.category, m.sub_category).label,
      color: categoryMeta(m.category, m.sub_category).color,
    })),
  };
};

/**
 * A reading counts as escalated when it was escalated in Readings Review. The
 * List Report is escalated-readings-only (p.joshi), but the backend list-report
 * endpoint currently returns non-escalated readings too, so we filter on the
 * per-reading flag here. The backend field is the (misspelled) `esclated`; the
 * correctly spelled variants are accepted defensively in case it is fixed later.
 */
export const isEscalated = (reading) =>
  reading?.esclated === true ||
  reading?.escalated === true ||
  Boolean(reading?.escalated_at);

/**
 * List Report -> list rows.
 * Readings are grouped by their OWN reading_type (heart rate is never its own
 * group; it shows as the secondary "bpm" on BP/pulse-ox rows), sorted
 * newest-first, matching the List view contract. Only escalated readings are
 * kept (the List view shows escalated readings only); patients left with no
 * escalated readings are dropped so no empty rows render.
 * Row: { ...patient, vitals: [{ key, label, readings: [listItem] }] }
 */
export const adaptListRows = (apiData) => {
  const results = apiData?.results || [];
  return results
    .map((result) => {
      const byVital = new Map();
      (result.readings || []).forEach((reading) => {
        const key = reading.reading_type;
        if (!VITALS_BY_KEY[key]) return;
        if (!isEscalated(reading)) return; // List view = escalated readings only
        const list = byVital.get(key) || [];
        list.push(buildListItem(reading));
        byVital.set(key, list);
      });

      const vitals = VITAL_ORDER.filter((key) => byVital.has(key)).map((key) => ({
        key,
        label: VITALS_BY_KEY[key].label,
        readings: byVital
          .get(key)
          .sort((a, b) => (a._ts < b._ts ? 1 : a._ts > b._ts ? -1 : 0)),
      }));

      return { ...patientFields(result), vitals };
    })
    .filter((row) => row.vitals.length > 0);
};

/**
 * Flatten a List Report payload to one row per reading for the Excel export
 * ("just the readings", per p.joshi). Escalated readings only, matching the
 * List report it represents. Shape matches what generateReadingsExcel consumes.
 */
export const flattenListReadings = (apiData) => {
  const results = apiData?.results || [];
  const rows = [];
  results.forEach((result) => {
    const p = result.patient || {};
    (result.readings || []).forEach((reading) => {
      if (!isEscalated(reading)) return; // Excel = escalated readings only
      rows.push({
        patientId: p.id,
        patientName: p.name || "",
        dob: formatDob(p.dob),
        providerName: p.provider?.name || "",
        readingTypeKey: reading.reading_type,
        readingType: reading.category_label || reading.reading_type,
        value: reading.value,
        recordedAt: reading.recorded_at,
        categories: (reading.measurements || [])
          .map((m) => m.sub_category_label)
          .filter(Boolean)
          .join(", "),
        outOfRange: reading.out_of_range ? "Yes" : "No",
        note: result.latest_note?.text || "",
      });
    });
  });
  return rows;
};

/**
 * Vital keys present in category/list rows, for the legend and the PDF
 * legend. List rows carry heart rate inline on BP/pulse-ox readings, so its
 * card is surfaced whenever one of those pairs is present. Rows without
 * vitals (Extended Threshold) contribute nothing.
 */
export const collectVitalKeys = (rows) => {
  const present = new Set();
  (rows || []).forEach((row) =>
    (row.vitals || []).forEach((v) => {
      present.add(v.key);
      v.readings?.forEach((r) => {
        if (r.secondary) present.add("heart_rate");
      });
    })
  );
  return Array.from(present);
};

/**
 * Extended Threshold Report snapshot -> rows (SCRUM-790). The backend
 * pre-sorts `results` (Hypertensive Care, Stage 2, Hypotension; more days
 * first; then name) and owns `reason` and `highlight`, so the order and both
 * values are kept exactly as sent.
 * Row: { ...patient, category, categoryLabel, reason, qualifyingDays,
 *        consecutiveCount, highlight }
 */
export const adaptExtendedThresholdRows = (snapshot) => {
  const results = snapshot?.results || [];
  return results.map((result) => ({
    ...patientFields(result),
    email: result.patient?.email || "",
    category: result.category || null,
    categoryLabel: result.category_label || "",
    reason: result.reason || "",
    qualifyingDays: result.qualifying_days ?? null,
    consecutiveCount: result.consecutive_count ?? null,
    highlight: result.highlight || null,
  }));
};
